import { AudiencePageLayout, type AudienceContent } from '../audience/AudiencePageLayout';

const content: Record<'de' | 'en', AudienceContent> = {
  de: {
    title: 'qtplan – Discovery: Vom Board zum Projektplan | Canvas, Schätzungen & Konsequenzen',
    metaDesc: 'Die Discovery ist der entscheidende Moment jeder Projektphase. qtplan verbindet Board, Schätzungen und Konsequenzen — vom ersten Brainstorming bis zum belastbaren Plan.',
    hero: {
      h1: 'Scope klären. Team zusammenstellen. Risiken aufdecken.',
      lead: 'Am Anfang jeder Projektphase steht dieselbe Frage: Was brauchen wir, wer kann es, wie lange dauert es? qtplan ersetzt drei bis vier separate Tools weil Board, Schätzungen, Skills und Konsequenzen zusammenhängen. Die Discovery wird nicht weggeworfen sondern zum Projektplan weiterentwickelt.',
    },
    bento: {
      heading: 'Von der Idee zum belastbaren Plan',
      items: [
        {
          span: 2,
          label: 'BOARD',
          title: 'Freifläche statt starrer Spalten',
          body: 'Das Board ist eine Freifläche mit flexiblen Lanes, Info-Boxen und beliebiger Struktur pro Board. Nicht "To Do / In Progress / Done" sondern das was die Discovery gerade braucht. Tasks werden direkt platziert — mit Schätzungen, Skill-Anforderungen und Konsequenzen. Die Struktur entsteht organisch während des Gesprächs.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'ROADMAP',
          title: 'Timeline aus denselben Daten',
          body: 'Wenn die Discovery steht, werden dieselben Tasks zur Gantt-Timeline. Kein Abtippen, kein Export. Verschiebungen, Abhängigkeiten und Konfidenzintervalle sind sofort sichtbar. Die Roadmap ist immer aktuell weil sie auf denselben Daten basiert.',
        },
        {
          span: 1,
          label: 'ÜBERSICHT',
          title: 'Konsequenzen auf einer Seite',
          body: 'Die aggregierte Übersicht fasst Hard Requirements (verbindliche Termine, technische Vorgaben) und Soft Requirements (wünschenswert aber verhandelbar) zusammen — mit Deadlines, verlinkten Tasks und dokumentierten Konsequenzen. "Schneller" bedeutet höheres Risiko. "Weniger Leute" bedeutet längere Timeline. "Mehr Scope" — was wird verdrängt? Alles explizit, alles auf einer Seite.',
        },
        {
          span: 2,
          label: 'PERSPEKTIVEN',
          title: 'Verschiedene Rollen, verschiedene Sichten',
          body: 'Engineers geben individuelle Schätzungen ab: "Für mich dauert das länger weil ich das noch nie gemacht habe." Tech-Leads sehen Skill-Lücken und Doppelbuchungen: "Wir brauchen jemanden mit Erfahrung in X, und Maria kann nicht gleichzeitig auf drei Projekten arbeiten." Stakeholder sehen Timeline und Kosten — und können selbst ausprobieren was "schneller", "weniger Leute" oder "mehr Scope" konkret bedeutet. qtplan zeigt die Konsequenzen jeder dieser Fragen.',
        },
        {
          span: 1,
          label: 'RISIKEN',
          title: 'Risiko als Zahl statt als Gefühl',
          body: 'PERT-Schätzungen machen Unsicherheit messbar. Wenn Best Case und Worst Case weit auseinander liegen, ist das Risiko hoch — und das sieht man sofort. Auf dem Board, in der Timeline, in der Übersicht. Risiko wird sichtbar bevor es zum Problem wird.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'KONSEQUENZEN',
          title: 'Was passiert wenn nicht',
          body: 'Jeder Task kann Konsequenzen dokumentieren — was passiert wenn er nicht umgesetzt wird, was wenn er später kommt. Diese Konsequenzen fließen in die Übersicht ein und machen Trade-offs explizit statt implizit.',
        },
        {
          span: 1,
          label: 'CANVAS',
          title: 'Infrastruktur und Abhängigkeiten visualisieren',
          body: 'Der Canvas bietet eine Freifläche mit Layern, Gruppen, Verbindungen und Sprite-Katalog. Projektstruktur, Tech-Trees, Infrastruktur — als lebendiges Diagramm für Kickoffs und Discovery-Sessions.',
        },
        {
          span: 2,
          label: 'ITERATIV',
          title: 'Kein Wegwerf-Artefakt',
          body: 'Die Discovery ist nicht ein Meeting das ein Dokument produziert. Sie ist der Startpunkt eines Plans der kontinuierlich verfeinert wird. Tasks bekommen Schätzungen, Schätzungen werden präziser, Zuordnungen werden angepasst. Der Übergang von Discovery zu Projektplan ist fließend — alles im selben Tool.',
          variant: 'muted',
        },
      ],
    },
    narrative: {
      heading: 'Warum die Discovery der entscheidende Moment ist',
      paragraphs: [
        'Die meisten Projekte scheitern nicht an der Umsetzung sondern an der Planung. Scope wird nicht geklärt, Risiken nicht aufgedeckt, Abhängigkeiten nicht erkannt. Die Discovery ist der Moment in dem diese Fehler vermieden werden können — oder in dem sie zementiert werden.',
        'In der Praxis sieht die Discovery so aus: Miro für die Struktur, Spreadsheet für die Schätzungen, Jira für die Tasks, nochmal ein Spreadsheet für die Skills. Vier Tools, vier Kontexte, null Zusammenhang. Jede Änderung muss manuell in jedem Tool nachgezogen werden. Am Ende ein Whiteboard-Foto, ein Confluence-Dokument das niemand aktualisiert und ein Projektplan der in einem anderen Tool von Null beginnt. Der Bruch zwischen Discovery und Planung ist der Moment in dem Information verloren geht.',
        'qtplan beseitigt diesen Bruch. Ein Task hat gleichzeitig eine Position auf dem Board, eine PERT-Schätzung, Skill-Anforderungen und Konsequenzen. Änderungen wirken sofort überall. Die Discovery deckt auf was sonst erst im Sprint sichtbar wird: Ressourcen-Engpässe weil dieselben Schlüsselleute auf mehreren Projekten eingeplant sind. Skill-Lücken weil niemand geprüft hat ob die vorhandene Expertise reicht. Risiken die in divergierenden Schätzungen stecken. Konsequenzen die niemand dokumentiert hat. Wenn die Discovery steht, werden dieselben Daten zur Timeline, zur Übersicht, zum Plan.',
        'Das bedeutet nicht dass die Discovery abgeschlossen ist. Schätzungen werden präziser, neue Risiken tauchen auf, Zuordnungen ändern sich. Aber der Plan wird verfeinert statt neu gebaut. Die Discovery ist kein Wegwerf-Artefakt sondern der Startpunkt eines lebendigen Plans.',
      ],
    },
    cta: {
      heading: 'Discovery starten.',
      body: 'Kein Account. Keine Cloud. Alle Daten bleiben lokal im Browser. Board erstellen, Tasks platzieren und erleben wie die Discovery zum Projektplan wird.',
    },
  },
  en: {
    title: 'qtplan – Discovery: From Board to Project Plan | Canvas, Estimates & Consequences',
    metaDesc: 'The discovery is the decisive moment of every project phase. qtplan connects board, estimates, and consequences — from the first brainstorm to a reliable plan.',
    hero: {
      h1: 'Clarify scope. Assemble the team. Uncover risks.',
      lead: 'At the start of every project phase, the same question arises: What do we need, who can do it, how long will it take? qtplan replaces three to four separate tools because board, estimates, skills, and consequences are interconnected. The discovery is not thrown away but evolved into the project plan.',
    },
    bento: {
      heading: 'From Idea to Reliable Plan',
      items: [
        {
          span: 2,
          label: 'BOARD',
          title: 'Free-Form Canvas Instead of Rigid Columns',
          body: 'The board is a free-form canvas with flexible lanes, info boxes, and any structure per board. Not "To Do / In Progress / Done" but whatever the discovery needs right now. Tasks are placed directly — with estimates, skill requirements, and consequences. The structure emerges organically during the conversation.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'ROADMAP',
          title: 'Timeline from the Same Data',
          body: 'When the discovery is done, the same tasks become the Gantt timeline. No retyping, no export. Shifts, dependencies, and confidence intervals are immediately visible. The roadmap is always up-to-date because it is based on the same data.',
        },
        {
          span: 1,
          label: 'OVERVIEW',
          title: 'Consequences on One Page',
          body: 'The aggregated overview summarizes hard requirements (binding deadlines, technical constraints) and soft requirements (desirable but negotiable) — with deadlines, linked tasks, and documented consequences. "Faster" means higher risk. "Fewer people" means a longer timeline. "More scope" — what gets displaced? Everything explicit, everything on one page.',
        },
        {
          span: 2,
          label: 'PERSPECTIVES',
          title: 'Different Roles, Different Views',
          body: 'Engineers contribute individual estimates: "This takes me longer because I have never done it before." Tech leads see skill gaps and double bookings: "We need someone with experience in X, and Maria cannot work on three projects at the same time." Stakeholders see timeline and cost — and can themselves explore what "faster," "fewer people," or "more scope" concretely means. qtplan shows the consequences of each of these questions.',
        },
        {
          span: 1,
          label: 'RISKS',
          title: 'Risk as a Number, Not a Feeling',
          body: 'PERT estimates make uncertainty measurable. When best case and worst case are far apart, risk is high — and that is immediately visible. On the board, in the timeline, in the overview. Risk becomes visible before it becomes a problem.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'CONSEQUENCES',
          title: 'What Happens If Not',
          body: 'Every task can document consequences — what happens if it is not implemented, what if it comes later. These consequences flow into the overview and make trade-offs explicit instead of implicit.',
        },
        {
          span: 1,
          label: 'CANVAS',
          title: 'Visualize Infrastructure and Dependencies',
          body: 'The canvas provides a free-form surface with layers, groups, connections, and a sprite catalog. Project structure, tech trees, infrastructure — as a living diagram for kickoffs and discovery sessions.',
        },
        {
          span: 2,
          label: 'ITERATIVE',
          title: 'Not a Throwaway Artifact',
          body: 'The discovery is not a meeting that produces a document. It is the starting point of a plan that is continuously refined. Tasks get estimates, estimates become more precise, assignments are adjusted. The transition from discovery to project plan is seamless — all in the same tool.',
          variant: 'muted',
        },
      ],
    },
    narrative: {
      heading: 'Why Discovery Is the Decisive Moment',
      paragraphs: [
        'Most projects do not fail at execution but at planning. Scope is not clarified, risks not uncovered, dependencies not recognized. The discovery is the moment where these mistakes can be avoided — or where they get cemented.',
        'In practice, the discovery looks like this: Miro for the structure, a spreadsheet for the estimates, Jira for the tasks, another spreadsheet for the skills. Four tools, four contexts, zero connection. Every change must be manually updated in each tool. The result: whiteboard photos, a Confluence document that nobody updates, and a project plan that starts from scratch in a different tool. The break between discovery and planning is the moment where information is lost.',
        'qtplan eliminates this break. A task simultaneously has a position on the board, a PERT estimate, skill requirements, and consequences. Changes take effect everywhere immediately. The discovery uncovers what otherwise only becomes visible mid-sprint: resource bottlenecks because the same key people are scheduled across multiple projects. Skill gaps because nobody checked whether existing expertise is sufficient. Risks hidden in diverging estimates. Consequences that nobody documented. When the discovery is complete, the same data becomes the timeline, the overview, the plan.',
        'That does not mean the discovery is finished. Estimates become more precise, new risks emerge, assignments change. But the plan is refined rather than rebuilt. The discovery is not a throwaway artifact but the starting point of a living plan.',
      ],
    },
    cta: {
      heading: 'Start the discovery.',
      body: 'No account. No cloud. All data stays local in the browser. Create a board, place tasks, and experience how the discovery becomes the project plan.',
    },
  },
};

export function DiscoveryPage({ lang }: { lang: 'de' | 'en' }) {
  return <AudiencePageLayout lang={lang} slug="discovery" content={content[lang]} basePath="features" />;
}
